<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Users Management
            </h1>
            <button @click="openModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add User
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by name or email..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Role Filter -->
                    <SearchableSelect
                        v-model="filters.role"
                        :options="availableRoles"
                        placeholder="All Roles"
                        option-label="name"
                        option-value="name"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchUsers"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.status"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchUsers"
                    />

                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="userStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="5" />
        </div>

        <!-- Error State -->
        <div
            v-if="userStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <TriangleAlert class="h-5 w-5 text-danger-400" />
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ userStore.error }}
                    </p>
                    <button
                        @click="userStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!userStore.loading && !userStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Name
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Email
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Roles
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="user in userStore.users"
                            :key="user.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div class="flex items-center">
                                    <div class="flex-shrink-0 h-10 w-10">
                                        <div
                                            class="h-10 w-10 rounded-full bg-primary-500 flex items-center justify-center"
                                        >
                                            <span
                                                class="text-white font-medium"
                                            >
                                                {{
                                                    user.name
                                                        .charAt(0)
                                                        .toUpperCase()
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="ml-4">
                                        <div
                                            class="text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {{ user.name }}
                                        </div>
                                        <div
                                            class="text-sm text-gray-500 dark:text-gray-400"
                                        >
                                            {{ user.email }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ user.email }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="user.status"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Active
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                >
                                    Inactive
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div class="flex flex-wrap gap-1">
                                    <span
                                        v-for="role in user.roles"
                                        :key="role.id"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                    >
                                        {{ role.name }}
                                    </span>
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editUser(user)"
                                    title="Edit User"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(user)"
                                    title="Delete User"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="userStore.users.length === 0"
                :icon="Users"
                title="No users found"
                description="Get started by adding a new user."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add User
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="userStore.meta"
                :per-page="userStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- User Modal -->
        <UserModal
            v-if="showModal"
            :user="selectedUser"
            :is-editing="isEditing"
            @close="closeModal"
            @saved="handleSaved"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="deleteModalTitle"
            :message="deleteModalMessage"
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useUserStore } from "@/stores/userStore";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import UserModal from "@/components/UserModal.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import NotificationService from "@/utils/notifications";
import roleApi from "@/api/role";
import type { User } from "@/types/auth";
import {
    Pencil,
    Trash2,
    Users,
    RotateCcw,
    TriangleAlert,
    Plus,
} from "@lucide/vue";

const userStore = useUserStore();
// State
const searchQuery = ref("");
const filters = ref({
    status: null as string | null,
    role: undefined,
});
const availableRoles = ref<{ id: number; name: string }[]>([]);
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedUser = ref<User | null>(null);
const isEditing = ref(false);
const userToDelete = ref<User | null>(null);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this user? This action cannot be undone.",
);

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.status !== null ||
        filters.value.role !== undefined ||
        userStore.page !== 1 ||
        userStore.perPage !== 10
    );
});

// Options for Status filter
const statusOptions = [
    { value: null, label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
];

// Methods
const handlePerPageChange = (value: number) => {
    userStore.perPage = value;
    userStore.page = 1;
    fetchUsers();
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value.status = null;
    filters.value.role = undefined;
    userStore.page = 1;
    userStore.perPage = 10;
    fetchUsers();
};

const fetchUsers = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await userStore.fetchUsers(searchFilters);
};

const debouncedSearch = useDebounce(fetchUsers, 300);

const openModal = () => {
    selectedUser.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editUser = (user: User) => {
    selectedUser.value = user;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedUser.value = null;
    isEditing.value = false;
};

const confirmDelete = (user: User) => {
    userToDelete.value = user;
    deleteModalTitle.value = "Delete User";
    deleteModalMessage.value = `Are you sure you want to delete ${user.name}? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    userToDelete.value = null;
};

const handleSaved = () => {
    // Refresh users list to show the new/updated user
    fetchUsers();
    showNotification(
        isEditing.value
            ? "User updated successfully"
            : "User created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (userToDelete.value) {
            await userStore.deleteUser(userToDelete.value.id);
            showNotification("User deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete user", "error");
    }
};

const goToPage = (page: number) => {
    userStore.setPage(page);
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(async () => {
    await fetchRoles();
    fetchUsers();
});

const fetchRoles = async () => {
    try {
        const response = await roleApi.getRoles({ per_page: 100 });
        availableRoles.value = (response.data?.items ||
            response.data ||
            []) as { id: number; name: string }[];
    } catch (error) {
        console.error("Failed to fetch roles:", error);
        availableRoles.value = [];
    }
};
</script>
